import {
  AfterViewInit,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  Type,
  ViewChild,
} from '@angular/core';
import { EMMAChartDirective } from '@emma-directives/emma-chart.directive';

import { EMMABarsChartComponent } from './emma-barschart.component';
import { EMMABigDescriptedValueComponent } from './emma-bigdescriptedvalue.component';
import { EMMAChartBase } from './emma-chart-base';
import { ChartType } from './emma-chart.constant';
import { EMMACoverChartComponent } from './emma-coverchart.component';
import { EMMADonutChartComponent } from './emma-donutchart.component';
import { EMMAFunnelChartComponent } from './emma-funnelchart.component';
import { EMMAHalfDonutChartComponent } from './emma-halfdonutchart.component';
import { EMMALinesChartComponent } from './emma-lineschart.component';
import { EMMAMapChartComponent } from './emma-mapchart.component';
import { EMMAMiniChartComponent } from './emma-minichart.component';
import { EMMAQuickstatComponent } from './emma-quickstat.component';
import { EMMAStackBarsChartComponent } from './emma-stackbarschart.component';
import { EMMATrapezoidChartComponent } from './emma-trapezoidchart.component';
import { set } from 'lodash';

const charts: { [k in ChartType]: Type<EMMAChartBase> } = {
  quickstat: EMMAQuickstatComponent,
  donut: EMMADonutChartComponent,
  cover: EMMACoverChartComponent,
  lines: EMMALinesChartComponent,
  mini: EMMAMiniChartComponent,
  halfdonut: EMMAHalfDonutChartComponent,
  bars: EMMABarsChartComponent,
  stackbars: EMMAStackBarsChartComponent,
  value: EMMABigDescriptedValueComponent,
  funnel: EMMAFunnelChartComponent,
  trapezoid: EMMATrapezoidChartComponent,
  map: EMMAMapChartComponent,
};

@Component({
    selector: 'emma-chart',
    template: ` <ng-template emmaChartHost></ng-template> `,
    standalone: false
})
export class EMMAChartComponent extends EMMAChartBase implements OnInit, OnChanges, AfterViewInit {
  @Input()
  override type!: ChartType;

  @ViewChild(EMMAChartDirective, { static: true })
  chartHost!: EMMAChartDirective;
  @Output()
  override chartLoad = new EventEmitter<boolean>();

  @Output()
  override exported = new EventEmitter<string>();

  // Deprecated interface
  // addAxis: any;
  // addLine: any;
  // addDataset: any;
  // addLegend: any;

  viewContainerRef: any;
  componentRef: ComponentRef<EMMAChartBase> | undefined;

  // To catch initial settings automatically
  private _cachedChanges: SimpleChanges | undefined;
  initAttempts = 0;

  constructor(private componentFactoryResolver: ComponentFactoryResolver) {
    super();
  }

  ngOnInit(): void {
    if (this._cachedChanges) {
      this.ngOnChanges(this._cachedChanges);
    }
  }

  ngAfterViewInit() {
    this.safeInit();
  }

  /**
   * Initializes charts retrying while the library is loading
   */
  safeInit(): void {
    if (this.init) {
      try {
        ++this.initAttempts;
        this.init();
      } catch (ex) {
        if (this.initAttempts < 200) {
          setTimeout(() => this.init(), 10);
        } else {
          throw ex;
        }
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Detect first iteration
    const isInit = !this._cachedChanges;
    this._cachedChanges = Object.assign({}, this._cachedChanges || {}, changes);

    if (!isInit) {
      if (changes.chartId || changes.type || changes.layout || !this.componentRef) {
        this.createChart();
        if (this.componentRef) {
          this.copyChanges(this._cachedChanges, this.componentRef);
        }
      } else if (this.componentRef) {
        this.copyChanges(changes, this.componentRef);
      }
      this.safeInit();
    }
  }

  getChart() {
    return this.componentRef && this.componentRef.instance;
  }

  createChart() {
    this.viewContainerRef = undefined;
    this.componentRef = undefined;
    if (charts[this.type]) {
      const componentFactory = this.componentFactoryResolver.resolveComponentFactory(charts[this.type]);
      this.viewContainerRef = this.chartHost.viewContainerRef;
      this.viewContainerRef.clear();
      this.componentRef = this.viewContainerRef.createComponent(componentFactory);
      if (this.componentRef) {
        this.copyInterface(this.componentRef.instance);
      }
    } else {
      throw new Error('Chart type not found: ' + this.type);
    }
  }

  copyInterface(instance: any) {
    for (const prop of [
      'update',
      'updateTotal',
      'updateVariation',
      'updateRelative',
      'loading',
      'loaded',
      'addLegend',
      'init',
      'removeAxis',
      'removeLine',
    ]) {
      if (typeof instance[prop] === 'function') {
        set(this, prop, instance[prop].bind(instance));
      } else {
        set(this, prop, instance[prop]);
      }
    }
    if (instance.chartLoad) {
      instance.chartLoad.subscribe((v: boolean) => {
        this.chartLoad.emit(v);
      });
    }
    if (instance.exported) {
      instance.exported.subscribe((image: string) => {
        this.exported.emit(image);
      });
    }
  }

  copyChanges = (changes: SimpleChanges, componentRef: ComponentRef<EMMAChartBase>) => {
    const props = Object.keys(changes);
    for (const prop of props) {
      set(componentRef.instance, prop, changes[prop].currentValue);
    }
  };
}
